import React, { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import { ButtonBar, ScreenHeader } from '../../components';
import { useScrollToTop } from '../../hooks';

import { NewPOAContext } from './new-poa-composer';

export function NewPOAGrantingPowersComplete() {
  const { powerOfAttorney } = useContext(NewPOAContext);
  const navigate = useNavigate();
  
  useScrollToTop();
  
  const handleBack = useCallback(() => {
    navigate('../gift-recipients');
  }, [ navigate ]);

  const handleNext = useCallback(() => {
    navigate('../period-reports');
  }, [ navigate ]);

  // Count granted powers
  const grantedPowers = [
    powerOfAttorney.real_estate_power,
    powerOfAttorney.tangible_personal_property,
    powerOfAttorney.securities_transactions,
    powerOfAttorney.banking_transactions,
    powerOfAttorney.operating_business,
    powerOfAttorney.digital_assets,
    powerOfAttorney.insurance_annuity,
    powerOfAttorney.estate_trust_transactions,
    powerOfAttorney.living_trust_transfers,
    powerOfAttorney.legal_actions,
    powerOfAttorney.personal_family_care,
    powerOfAttorney.pet_care,
    powerOfAttorney.government_benefits,
    powerOfAttorney.retirement_plan_transactions,
    powerOfAttorney.tax_matters
  ].filter(Boolean).length;
  
  return (
    <div className="new-poa-granting-powers-complete screen-container screen">
      <ScreenHeader
        title="Granting Powers Complete"
        description="Review your power assignments before continuing"
        handleBack={handleBack}
      />
      
      <div className="content-section">
        <div className="completion-summary">
          <div className="summary-card">
            <div className="summary-icon">✅</div>
            <div className="summary-content">
              <h3>Powers Assignment Complete</h3>
              <p>You have successfully configured the powers for your agent.</p>
            </div>
          </div>

          <div className="powers-overview">
            <h3>Powers Summary</h3>
            <div className="overview-stats">
              <div className="stat-item">
                <span className="stat-number">{grantedPowers}</span>
                <span className="stat-label">Powers Granted</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{15 - grantedPowers}</span>
                <span className="stat-label">Powers Not Granted</span>
              </div>
            </div>

            <div className="powers-categories">
              <h4>Key Areas Covered:</h4>
              <ul className="powers-list">
                {powerOfAttorney.banking_transactions && <li>Banking and Financial Transactions</li>}
                {powerOfAttorney.real_estate_power && <li>Real Estate Transactions</li>}
                {powerOfAttorney.securities_transactions && <li>Investment and Securities Management</li>}
                {powerOfAttorney.tax_matters && <li>Tax Matters and Filing</li>}
                {powerOfAttorney.insurance_annuity && <li>Insurance and Annuity Management</li>}
                {powerOfAttorney.legal_actions && <li>Legal Actions and Representation</li>}
                {powerOfAttorney.personal_family_care && <li>Personal and Family Care</li>}
              </ul>
            </div>

            {powerOfAttorney.allow_gifts && (
              <div className="gift-summary">
                <h4>Gift Authorizations:</h4>
                <ul className="gift-list">
                  <li>Gift-making powers: Enabled</li>
                  {powerOfAttorney.gifts_to_agent && <li>Gifts to agent: Authorized</li>}
                  {powerOfAttorney.max_gift_amount_per_year && (
                    <li>Maximum gift amount per year: ${powerOfAttorney.max_gift_amount_per_year}</li>
                  )}
                  <li>Gifts to others: {powerOfAttorney.gifts_to_others || 'Not specified'}</li>
                </ul>
              </div>
            )}
          </div>

          <div className="next-steps">
            <h3>Next Steps</h3>
            <p>
              Now we'll configure additional options for your Power of Attorney, 
              including reporting requirements, agent compensation, and other 
              important settings.
            </p>
          </div>
        </div>
      </div>

      <ButtonBar 
        handlePrimary={handleNext}
        primaryText="Continue to Additional Options"
      />
    </div>
  );
}
